import React from 'react'
import { axiosGet, axiosPost } from '../../common/js/axios-utility'
import Cookies from 'js-cookie';
import { PurchaseContext } from '../contexts/PurchaseContext';

import Button from '../../common/components/Button';
import ReinstatementModal from '../components/ReinstatementModal'
import { errorToast } from '../../common/js/toast-utility';
import { type WebProduct } from '../../common/types/webshop.types';
import { type SerialOption } from '../../common/types/webshop.types';
import { addToCart } from '../../common/js/cart-utility';
import { useTranslation } from 'react-i18next';

import OpticsLooksUpsell from '../../common/components/upsell-modal/OpticsLooksUpsell';
import { type ModalHandle } from '../../common/components/Modal';
import Modal from '../../common/components/Modal';

type upsellModal = 'opticsLooks' | 'suite';

declare const _hsq: any;

type Props = {
    variant: WebProduct,
    quantity: number,
    serial: SerialOption,
    renewalVariants: Record<string, WebProduct>,
    label: string,
    onComplete: Function,
    currencyIso: string
}

export default function AddToCartButton(props: Props) {
    const { t } = useTranslation('common')

    const {variant, quantity, serial, renewalVariants, label=t('actions.addToCart'), currencyIso} = props;
    const [modal, setModal] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    
    const [purchaseState] = React.useContext(PurchaseContext);
    const [upsellModalName, setUpsellModalName] = React.useState('');

    const upsellModalRef = React.useRef<ModalHandle>(null);
    const reinstatementModalRef = React.useRef<ModalHandle>(null);

    if (!variant){
        return <></>
    }

    const isRenewalVariant = renewalVariantTitles.includes(variant.type);
    const serialNum = serial ? serial.serial : '';
    const contractId = serial ? serial.contractId : '';

    const {appType} = purchaseState;

    const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        try{
            setSubmitting(true);
            if (isRenewalVariant && serial.isLegacy){
                reinstatementModalRef.current!.show();
                return;
            }

            else{
                await handleAddToCart(variant.sku, quantity, contractId, serialNum);
                setSubmitting(false);
            }
        }
        catch(err){
            console.error(err);
            errorToast('We were unable to add this item to your cart. Please try again.')
            setSubmitting(false);
        }
    }

    const handleAddToCart = async (sku: string, quantity: number, contractId: string, serial: string) => {
        const hasUpsell = processUpsell();
        if (hasUpsell){
            return;
        } 
        const cartNew = await addToCart(sku, quantity, contractId, serial, 'web', currencyIso);
        await applyAutoDiscounts(cartNew.id);

        //gaAddToCartEvent(variant, self.quantity);
        if (appType == 'compact'){
            hsBuyNowAbTest(false);
        }
        const showEvent = new Event('show-cart');
        window.dispatchEvent(showEvent);
    }

    const handleReinstatementConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
        const varId = renewalVariants.reinstatement.sku
        await handleAddToCart(varId, quantity, contractId, serialNum);
        reinstatementModalRef.current!.hide();
        setSubmitting(false);
    }
    const handleReinstatementCancel = () => {
        reinstatementModalRef.current!.hide();
        setSubmitting(false);
    }

    const processUpsell = (): boolean => {
        const prod: WebProduct = variant;
        /*
        if (prod.sku == 'OPT-S'){
            launchUpsellModal('opticsLooks');
            return true;
        }
        */
        return false;
    }

    const launchUpsellModal = (modalName: upsellModal) => {
        setUpsellModalName(modalName);
        upsellModalref.current!.show();
    }
    const closeUpsellModal = () => {
        setUpsellModalName('');
        upsellModalref.current!.hide();
    }

    const applyAutoDiscounts = async (checkoutId: string) => {
        const bannerId = Cookies.get('pap-banner-id');

        if (purchaseState.promoUrl){
            await applyPromoDiscount(checkoutId, purchaseState.promoUrl);
        }
        else if (bannerId){
            await handlePapCheck(checkoutId, bannerId);
        }
    }

    //If user entered site from Discount Link, attempt to apply discount
    const applyPromoDiscount = async (checkoutId: string, promoUrl: string) => {
        try{
            const payload = {
                cartId: checkoutId,
                refType: 'link',
                discountRef: promoUrl
            };
            await axiosPost('/shop/discount/add', payload);
        }
        catch(err){
            console.error(err);
        }
    }

    //If user entered site from Affiliate Link, check for valid product in cart.
    const handlePapCheck = async (checkoutId: string, bannerId: string) => {
        try{
            const res = await axiosGet('/affiliate/pap_discount/' + bannerId);
            const code = res.data;
            const payload = {
                cartId: checkoutId,
                refType: 'code',
                discountRef: code
            }
            await axiosPost('/shop/discount/add', payload);
        }
        catch(err){
            console.error(err);
            return false;
        }

    }
    
    const hsBuyNowAbTest = (isBuyNow: boolean) => {
        try {
            _hsq.push(["trackCustomBehavioralEvent", {
               name: "pe21305637_buy_now_ab_test",
               properties: {
                  cta_type : isBuyNow ? 'Buy Now' : 'Add To Cart',
                  product : variant.familyName
               }}]);
        }
        catch(err) {
            console.error(err);
        }
    }

    let upsellModalContent = <></>
    
    return (
        <div className="add-to-cart-block">
            <Button processing={submitting} label={label} onClick={handleClick} type="button"
                className='btn add-to-cart-button' processingLabel="Please Wait..." disabled={!variant} />

            <Modal ref={reinstatementModalRef}>
                <ReinstatementModal renewalVariant={renewalVariants.renewal}
                    reinstatementVariant={renewalVariants.reinstatement} onConfirm={handleReinstatementConfirm} onCancel={handleReinstatementCancel} />
            </Modal>

            <Modal hideCloseButton ref={upsellModalRef} className='upsell-modal'>
                {upsellModalName && (upsellModalContent)}
            </Modal>
        </div>
    )

}

const renewalVariantTitles = ['U&S Renewal', 'Legacy U&S Renewal'];